import axios, { AxiosResponse } from 'axios'
import { firstValueFrom } from 'rxjs'

import { logAndCaptureException } from '../../../../utils/errorTools'
import { userClaimIds } from '../../../../utils/userClaims'
import { AuthStateWithAccessToken, transformUserInAuthState } from '../../../auth/state'
import { RewardsOnboardingObserverCallback } from './machine-rewards-onboarding'

export interface RewardsOnboardingPersistState {
  state: string
  context: Record<string, unknown>
}

export interface RewardsOnboardingPersistOptions {
  token: string
}

const apiUrl = '/api/rewards/onboarding'

const parseResponse = <T>(response: AxiosResponse<T>): T => {
  if (response.status !== 200) {
    throw response.data
  }
  return response.data
}

const updateState = async (
  stateToPersist: RewardsOnboardingPersistState,
  { token }: RewardsOnboardingPersistOptions,
): Promise<RewardsOnboardingPersistState> => {
  const response = await axios.post<RewardsOnboardingPersistState>(
    apiUrl,
    { data: stateToPersist },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return parseResponse(response)
}

const deleteState = async ({ token }: RewardsOnboardingPersistOptions): Promise<undefined> => {
  const response = await axios.delete(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  parseResponse(response)
  return undefined
}

const persistState = async (
  stateToPersist: RewardsOnboardingPersistState | undefined,
  { token }: RewardsOnboardingPersistOptions,
): Promise<RewardsOnboardingPersistState | undefined> => {
  // Note the `await`s are intentional, so error is thrown locally
  try {
    if (!stateToPersist) {
      return await deleteState({ token })
    }
    return await updateState(stateToPersist, { token })
  } catch (error) {
    console.error(`Onboard \`persistState\` error for URL "${apiUrl}": `, error)
    return undefined
  }
}

const transformUser = (state: RewardsOnboardingPersistState | undefined) =>
  transformUserInAuthState((user) => {
    user[userClaimIds.rewardsOnboarding] = state
    return user
  })

let previousState: RewardsOnboardingPersistState | undefined = undefined

export const persistRewardsOnboardingState =
  (authState: AuthStateWithAccessToken): RewardsOnboardingObserverCallback =>
  (state) => {
    if (!state.changed || !authState.isAuthenticated) {
      return
    }
    const stateToPersist =
      state.value === 'invalid'
        ? undefined
        : {
            state: typeof state.value === 'string' ? state.value : JSON.stringify(state.value),
            context: {},
          }

    // Compare with previousState to avoid redundant API calls
    if (
      previousState &&
      previousState.state === stateToPersist?.state &&
      JSON.stringify(previousState.context) === JSON.stringify(stateToPersist?.context)
    ) {
      return
    }

    return persistState(stateToPersist, { token: authState.accessToken })
      .then(() => {
        previousState = stateToPersist
        return firstValueFrom(transformUser(stateToPersist))
      })
      .catch((error) => {
        logAndCaptureException(error)
      })
  }
